import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ChannelService, ChatClientService, StreamI18nService } from 'stream-chat-angular';
import { StreamAction } from './stream.actions';
import { FORCE_LOGOUT_401, ResponseSuccessAuthLogoutAction } from '../auth/auth.actions';
import { filter, map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganisationDto, StreamService } from '@careapp-group/ng-sdk';
import { Organisations } from '../organisations/organisations.actions';
import { getAuthenticationState } from '../auth/auth.selectors';
import { userHasConversations } from '../conversations/conversations.selectors';
import { getStreamState } from '../reducers';
import { environment } from '../../../environments/environment';
import { userHasEnquiries } from '../../enquiries/store/enquiries.selectors';

@Injectable()
export class StreamEffects {
  constructor(
    private actions$: Actions<any>,
    private store: Store<any>,
    private streamChatClient: ChatClientService,
    private streamService: StreamService,
    private streamI18nService: StreamI18nService,
    private channelService: ChannelService,
  ) {}

  // When a token is successfully retrieved, request to initiate the channel service.
  initChatService = createEffect(() =>
    this.actions$.pipe(
      ofType(StreamAction.requestInitStream),
      withLatestFrom(
        this.store.select(getStreamState),
        this.store.select(getAuthenticationState),
        this.store.select(userHasConversations),
        this.store.select(userHasEnquiries),
      ),
      filter(
        ([action, streamState, authState, userHasConversations, userHasEnquiries]) =>
          (userHasConversations === true || userHasEnquiries === true) &&
          streamState.token != null &&
          !streamState.initiated,
      ),
      switchMap(
        async ([action, streamState, authState, userHasConversations, userHasEnquiries]) => {
          try {
            await this.streamChatClient.init(
              environment.streamApiKey,
              authState.auth.userId,
              streamState.token,
            );
            this.streamI18nService.setTranslation();
            await this.channelService.init(
              {
                members: { $in: [authState.auth.userId] },
                type: 'team',
              },
              null,
              null,
              false,
            );

            return StreamAction.responseSuccessInitStream();
          } catch (err) {
            return StreamAction.responseFailInitStream(err);
          }
        },
      ),
    ),
  );

  // When a token is successfully retrieved, request to initiate the stream service.
  initChatServiceOnToken = createEffect(() =>
    this.actions$.pipe(
      ofType(StreamAction.responseSuccessStreamToken),
      map((_) => StreamAction.requestInitStream()),
    ),
  );

  // If either Enquiries or conversations is enabled for the org:
  // When an org config response arrives where conversations is enabled, retrieve a
  // stream access token if none already exists.
  startRegistrationIfConfigAllows = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.RESPONSE_SUCCESS_OWN),
      withLatestFrom(this.store.select(getStreamState)),
      filter(
        ([action, streamState]) =>
          streamState.token == null &&
          (<OrganisationDto[]>action.payload).some(
            (org) => org.config.conversationsEnabled || org.features.hasEnquiries,
          ),
      ),
      map((_) => StreamAction.requestStreamToken()),
    ),
  );

  generateToken = createEffect(() =>
    this.actions$.pipe(
      ofType(StreamAction.requestStreamToken),
      withLatestFrom(this.store.select(getStreamState)),
      filter(([action, streamState]) => streamState.token == null),
      switchMap(([action, streamState]) => {
        return this.streamService.generateAccessToken0().pipe(
          map((res) => {
            return StreamAction.responseSuccessStreamToken({ token: res.token });
          }),
          catchError((res) => of(StreamAction.responseFailStreamToken(res))),
        );
      }),
    ),
  );

  handleLogout = createEffect(() =>
    this.actions$.pipe(
      ofType(ResponseSuccessAuthLogoutAction, FORCE_LOGOUT_401),
      switchMap(async (action) => {
        try {
          await this.streamChatClient.disconnectUser();
          return StreamAction.streamDisconnectedSuccess();
        } catch (err) {
          return StreamAction.streamDisconnectedFail(err);
        }
      }),
    ),
  );

  markAllRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StreamAction.requestMarkAllRead),
      switchMap(async (action) => {
        try {
          await this.streamChatClient.chatClient.markChannelsRead({});
          return StreamAction.successMarkAllRead();
        } catch (err) {
          return StreamAction.failMarkAllRead(err);
        }
      }),
    ),
  );
}
